import type { PreviewFeature } from "shared/PreviewFeatures";
import {
  ENABLE_ACCURACY_PAGE,
  ENABLE_APPEALS,
  ENABLE_HUMAN_REVIEW,
  ENABLE_LINK_SAFETY,
  ENABLE_PEOPLE_AND_FACES,
} from "shared/SharedConstants";
import {
  GO_SDK_PUBLISHED,
  JAVA_SDK_PUBLISHED,
  PHP_SDK_PUBLISHED,
  PYTHON_SDK_PUBLISHED,
  RUBY_SDK_PUBLISHED,
  RUST_SDK_PUBLISHED,
} from "src/lib/SdkConstants";
import docsTitles from "./DocsTitles.json";

/** A page about preview features lists them, and the sidebar marks it while any is in preview. */
export type DocsNavigationPage = {
  route: string;
  title: string;
  preview?: readonly PreviewFeature[];
};

export type DocsNavigationGroup = {
  id:
    | "start"
    | "guides"
    | "widgets"
    | "moderation"
    | "policies"
    | "assets"
    | "events"
    | "tools"
    | "projects"
    | "api"
    | "sdk";
  title: string;
  pages: DocsNavigationPage[];
};

/** Single source for docs sections, page order and visibility; titles come from MDX frontmatter. */
const groups: (Omit<DocsNavigationGroup, "pages"> & {
  pages: Omit<DocsNavigationPage, "title">[];
})[] = [
  {
    id: "start",
    title: "Start here",
    pages: [
      { route: "/docs/guides/choose-your-integration" },
      {
        route: "/docs",
      },
      {
        route: "/docs/how-it-works",
      },
      ...(ENABLE_ACCURACY_PAGE ? [{ route: "/docs/guides/accuracy" }] : []),
      {
        route: "/docs/guides/authentication",
      },
      {
        route: "/docs/pricing",
      },
      {
        route: "/docs/usage-limits",
      },
    ],
  },
  {
    id: "guides",
    title: "Guides",
    pages: [
      { route: "/docs/guides/upload-component" },
      { route: "/docs/guides/upload-without-server" },
      { route: "/docs/guides/moderate-comments" },
      { route: "/docs/guides/sensitive-media" },
      { route: "/docs/guides/native-age-checks" },
      { route: "/docs/guides/profile-pictures" },
    ],
  },
  {
    id: "widgets",
    title: "Widgets",
    pages: [
      { route: "/docs/widgets" },
      { route: "/docs/widgets/upload-widget" },
      { route: "/docs/widgets/media-component" },
    ],
  },
  {
    id: "moderation",
    title: "Moderation",
    pages: [
      {
        route: "/docs/guides/classifying-assets",
      },
      {
        route: "/docs/guides/moderation-categories",
      },
      {
        route: "/docs/guides/classification-results",
      },
      {
        route: "/docs/guides/moderation-policies",
      },
      { route: "/docs/guides/mandatory-rules" },
      { route: "/docs/guides/jurisdiction-rules" },
      {
        route: "/docs/guides/threaded-moderation",
      },
      ...(ENABLE_HUMAN_REVIEW ? [{ route: "/docs/guides/human-review" }] : []),
      ...(ENABLE_APPEALS ? [{ route: "/docs/guides/appeals" }] : []),
      {
        route: "/docs/guides/hash-lists",
      },
      {
        route: "/docs/guides/ocr",
      },
      ...(ENABLE_LINK_SAFETY
        ? [{ route: "/docs/guides/link-safety", preview: ["linkSafety"] as "linkSafety"[] }]
        : []),
      ...(ENABLE_PEOPLE_AND_FACES
        ? [
            {
              route: "/docs/guides/people-and-faces",
              preview: ["personDetection", "faceDetection"] as (
                | "personDetection"
                | "faceDetection"
              )[],
            },
          ]
        : []),
      {
        route: "/docs/guides/audio-and-video",
        preview: ["audio", "video"],
      },
      {
        route: "/docs/guides/websites",
        preview: ["website"],
      },
      {
        route: "/docs/guides/testing",
      },
    ],
  },
  {
    id: "policies",
    title: "Built-in policies",
    pages: [
      { route: "/docs/guides/built-in-policies" },
      { route: "/docs/guides/built-in-policies/general" },
      { route: "/docs/guides/built-in-policies/kids" },
      { route: "/docs/guides/built-in-policies/learning-and-support" },
      { route: "/docs/guides/built-in-policies/teen-safe" },
    ],
  },
  {
    id: "assets",
    title: "Assets and delivery",
    pages: [
      {
        route: "/docs/guides/delivering-assets",
      },
      {
        route: "/docs/guides/asset-permissions",
      },
      {
        route: "/docs/guides/lenses",
      },
      {
        route: "/docs/guides/asset-lifecycle",
      },
      {
        route: "/docs/guides/upload-presets",
      },
      {
        route: "/docs/guides/multipart-uploads",
      },
      {
        route: "/docs/guides/organising-assets",
      },
      {
        route: "/docs/guides/search",
      },
    ],
  },
  {
    id: "events",
    title: "Events and integrations",
    pages: [
      {
        route: "/docs/guides/webhooks",
      },
      {
        route: "/docs/guides/notifications",
      },
    ],
  },
  {
    id: "tools",
    title: "Assistants and CLI",
    pages: [
      {
        route: "/docs/guides/ai-assistants",
      },
      {
        route: "/docs/guides/local-development",
      },
    ],
  },
  {
    id: "projects",
    title: "Project management",
    pages: [
      {
        route: "/docs/guides/organisations",
      },
      {
        route: "/docs/guides/project-collaboration",
      },
    ],
  },
  {
    id: "api",
    title: "API reference",
    pages: [
      ...(ENABLE_APPEALS ? [{ route: "/docs/api/appeals" }] : []),
      {
        route: "/docs/api",
      },
      {
        route: "/docs/api/assets",
      },
      {
        route: "/docs/api/folders",
      },
      {
        route: "/docs/api/hash-lists",
      },
      {
        route: "/docs/api/policies",
      },
      {
        route: "/docs/api/lenses",
      },
      {
        route: "/docs/api/cdn",
      },
      {
        route: "/docs/api/events",
      },
      {
        route: "/docs/api/mcp",
      },
      {
        route: "/docs/guides/pagination-and-filtering",
      },
      {
        route: "/docs/guides/error-handling",
      },
      {
        route: "/docs/api/system",
      },
    ],
  },
  {
    id: "sdk",
    title: "SDKs",
    pages: [
      { route: "/docs/sdk" },
      { route: "/docs/examples" },
      {
        route: "/docs/sdk/javascript",
      },
      {
        route: "/docs/sdk/javascript/reference",
      },
      {
        route: "/docs/sdk/javascript/testing",
      },
      ...(PYTHON_SDK_PUBLISHED
        ? [
            { route: "/docs/sdk/python" },
            { route: "/docs/sdk/python/reference" },
            { route: "/docs/sdk/python/testing" },
          ]
        : []),
      ...(GO_SDK_PUBLISHED
        ? [
            { route: "/docs/sdk/go" },
            { route: "/docs/sdk/go/reference" },
            { route: "/docs/sdk/go/testing" },
          ]
        : []),
      ...(RUST_SDK_PUBLISHED
        ? [
            { route: "/docs/sdk/rust" },
            { route: "/docs/sdk/rust/reference" },
            { route: "/docs/sdk/rust/testing" },
          ]
        : []),
      ...(RUBY_SDK_PUBLISHED
        ? [
            { route: "/docs/sdk/ruby" },
            { route: "/docs/sdk/ruby/reference" },
            { route: "/docs/sdk/ruby/testing" },
          ]
        : []),
      ...(PHP_SDK_PUBLISHED
        ? [
            { route: "/docs/sdk/php" },
            { route: "/docs/sdk/php/reference" },
            { route: "/docs/sdk/php/testing" },
          ]
        : []),
      ...(JAVA_SDK_PUBLISHED
        ? [
            { route: "/docs/sdk/java" },
            { route: "/docs/sdk/java/reference" },
            { route: "/docs/sdk/java/testing" },
          ]
        : []),
    ],
  },
] as const;

export const DOCS_NAVIGATION: DocsNavigationGroup[] = groups.map((group) => ({
  ...group,
  pages: group.pages.map((page) => ({
    ...page,
    title: docsTitles[page.route as keyof typeof docsTitles],
  })),
}));
